import { useState, useEffect } from 'react';
import { X, Check, AlertCircle, CheckCircle, FileText, Wallet, Tag, TrendingUp, Sparkles, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { OFXParseResult } from '../utils/ofxParser';
import {
  deduplicateTransactions,
  TransactionToImport,
  DeduplicationResult
} from '../utils/transactionDeduplication';
import { useSmartCategorization } from '../hooks/useSmartCategorization';

interface TreasuryAccount {
  id: string;
  name: string;
  account_type: string;
  current_balance: number;
}

interface Category {
  id: string;
  name: string;
  type: 'income' | 'expense';
  is_internal_transfer: boolean;
}

interface EnhancedImportModalProps {
  parseResult: OFXParseResult | null;
  importedFileName: string;
  onClose: () => void;
  onConfirm: () => void;
  preselectedAccountId?: string;
  allowAccountSelection?: boolean;
  allowBalanceUpdate?: boolean;
}

export default function EnhancedImportModal({
  parseResult,
  importedFileName,
  onClose,
  onConfirm,
  preselectedAccountId,
  allowAccountSelection = true,
  allowBalanceUpdate = false
}: EnhancedImportModalProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(preselectedAccountId || null);
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [deduplicationResult, setDeduplicationResult] = useState<DeduplicationResult | null>(null);
  const [transactionsWithCategories, setTransactionsWithCategories] = useState<TransactionToImport[]>([]);
  const [showCategorization, setShowCategorization] = useState(false);
  const [updateBalance, setUpdateBalance] = useState(false);
  const [manualBalance, setManualBalance] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<Map<number, { categoryId: string; confidence: number }>>(new Map());
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [creatingCategories, setCreatingCategories] = useState(false);

  const smartCategorization = useSmartCategorization();

  const DEFAULT_CATEGORIES = [
    { name: 'Subventions', type: 'income' },
    { name: 'Ventes', type: 'income' },
    { name: 'Dons', type: 'income' },
    { name: 'Autres recettes', type: 'income' },
    { name: 'Salaires', type: 'expense' },
    { name: 'Fournitures', type: 'expense' },
    { name: 'Loyer', type: 'expense' },
    { name: 'Énergie', type: 'expense' },
    { name: 'Autres charges', type: 'expense' },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (parseResult && selectedAccountId !== undefined && categories.length > 0) {
      performDeduplication();
    }
  }, [parseResult, selectedAccountId, categories]);

  const loadInitialData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [accountsRes, categoriesRes] = await Promise.all([
        supabase
          .from('treasury_accounts')
          .select('id, name, account_type, current_balance')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('categories')
          .select('id, name, type, is_internal_transfer')
          .eq('user_id', user.id)
          .order('name')
      ]);

      if (accountsRes.data) setAccounts(accountsRes.data);
      if (categoriesRes.data) {
        console.log('[EnhancedImportModal] Loaded categories:', categoriesRes.data.length);
        setCategories(categoriesRes.data);
      }
      if (categoriesRes.error) {
        console.error('[EnhancedImportModal] Error loading categories:', categoriesRes.error);
      }
    } catch (error: any) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const performDeduplication = async () => {
    if (!parseResult?.transactions) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const transactionsToCheck: TransactionToImport[] = parseResult.transactions.map(t => ({
        date: t.date,
        label: t.label,
        amount: t.amount,
        treasury_account_id: selectedAccountId
      }));

      const result = await deduplicateTransactions(transactionsToCheck, user.id);
      setDeduplicationResult(result);
      setTransactionsWithCategories(result.newTransactions);

      if (result.newTransactions.length > 0 && categories.length > 0) {
        loadSmartSuggestions(result.newTransactions);
      }
    } catch (error: any) {
      console.error('Error during deduplication:', error);
    }
  };

  const loadSmartSuggestions = async (transactions: TransactionToImport[]) => {
    setLoadingSuggestions(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newSuggestions = new Map<number, { categoryId: string; confidence: number }>();

      for (let i = 0; i < transactions.length; i++) {
        const transaction = transactions[i];
        const suggestion = await smartCategorization.getSuggestionForTransaction(
          user.id,
          transaction.label
        );

        if (suggestion && suggestion.confidence >= 0.5) {
          newSuggestions.set(i, {
            categoryId: suggestion.categoryId,
            confidence: suggestion.confidence
          });
        }
      }

      setSuggestions(newSuggestions);
    } catch (error: any) {
      console.error('Error loading smart suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applyAllSuggestions = () => {
    const updated = [...transactionsWithCategories];
    suggestions.forEach((suggestion, index) => {
      if (index < updated.length) {
        updated[index].category_id = suggestion.categoryId;
      }
    });
    setTransactionsWithCategories(updated);
  };

  const applySuggestion = (index: number) => {
    const suggestion = suggestions.get(index);
    if (suggestion) {
      updateTransactionCategory(index, suggestion.categoryId);
    }
  };

  const createDefaultCategories = async () => {
    setCreatingCategories(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const categoriesToInsert = DEFAULT_CATEGORIES.map(cat => ({
        ...cat,
        user_id: user.id
      }));

      const { data, error } = await supabase
        .from('categories')
        .insert(categoriesToInsert)
        .select();

      if (error) {
        console.error('Error creating default categories:', error);
        alert('Erreur lors de la création des catégories');
        return;
      }

      if (data) {
        setCategories(data);
        console.log('[EnhancedImportModal] Created default categories:', data.length);
      }
    } catch (error: any) {
      console.error('Error creating default categories:', error);
      alert('Erreur lors de la création des catégories');
    } finally {
      setCreatingCategories(false);
    }
  };

  const updateTransactionCategory = (index: number, categoryId: string | null) => {
    const updated = [...transactionsWithCategories];
    updated[index].category_id = categoryId;
    setTransactionsWithCategories(updated);
  };

  const applyCategoryToAll = (categoryId: string | null) => {
    const updated = transactionsWithCategories.map(t => ({
      ...t,
      category_id: categoryId
    }));
    setTransactionsWithCategories(updated);
  };

  const handleImport = async () => {
    if (!deduplicationResult || deduplicationResult.newCount === 0) {
      alert('Aucune transaction à importer');
      return;
    }

    setIsImporting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const transactionsToInsert = transactionsWithCategories.map(t => ({
        user_id: user.id,
        date: t.date,
        label: t.label,
        amount: t.amount,
        category_id: t.category_id || null,
        notes: t.notes || '',
        imported_from: importedFileName,
        treasury_account_id: selectedAccountId || null,
        is_internal_transfer: false
      }));

      const { error: insertError } = await supabase
        .from('transactions')
        .insert(transactionsToInsert);

      if (insertError) throw insertError;

      if (updateBalance && selectedAccountId) {
        let balanceToSet: number | null = null;

        if (parseResult?.ledgerBalance !== undefined) {
          balanceToSet = parseResult.ledgerBalance;
        } else if (parseResult?.availableBalance !== undefined) {
          balanceToSet = parseResult.availableBalance;
        } else if (manualBalance) {
          balanceToSet = parseFloat(manualBalance);
        }

        if (balanceToSet !== null) {
          const { error: balanceError } = await supabase
            .from('treasury_accounts')
            .update({ current_balance: balanceToSet })
            .eq('id', selectedAccountId);

          if (balanceError) console.error('Error updating balance:', balanceError);
        }
      }

      onConfirm();
    } catch (error: any) {
      console.error('Error importing transactions:', error);
      alert(`Erreur lors de l'import: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  if (!parseResult) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getBalanceInfo = () => {
    if (parseResult.ledgerBalance !== undefined) {
      return {
        hasBalance: true,
        balance: parseResult.ledgerBalance,
        type: 'Solde comptable'
      };
    } else if (parseResult.availableBalance !== undefined) {
      return {
        hasBalance: true,
        balance: parseResult.availableBalance,
        type: 'Solde disponible'
      };
    }
    return { hasBalance: false };
  };

  const balanceInfo = getBalanceInfo();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Confirmation d'import</h3>
            <p className="text-sm text-gray-600 mt-1">{importedFileName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <FileText className="h-5 w-5" />
                    <span className="text-sm font-medium">Transactions détectées</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{parseResult.transactions.length}</p>
                </div>

                {deduplicationResult && (
                  <>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Nouvelles</span>
                      </div>
                      <p className="text-2xl font-bold text-green-900">{deduplicationResult.newCount}</p>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-orange-700 mb-2">
                        <AlertCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Doublons ignorés</span>
                      </div>
                      <p className="text-2xl font-bold text-orange-900">{deduplicationResult.duplicateCount}</p>
                    </div>
                  </>
                )}
              </div>

              {allowAccountSelection && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Compte de destination *
                  </label>
                  <select
                    value={selectedAccountId || ''}
                    onChange={(e) => setSelectedAccountId(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Non assigné (à assigner plus tard)</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({formatCurrency(account.current_balance)})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Les transactions importées seront rattachées à ce compte
                  </p>
                </div>
              )}

              {allowBalanceUpdate && selectedAccountId && balanceInfo.hasBalance && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Mise à jour du solde</span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updateBalance}
                        onChange={(e) => setUpdateBalance(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-700">Activer</span>
                    </label>
                  </div>
                  <p className="text-sm text-blue-700">
                    {balanceInfo.type} détecté : <span className="font-semibold">{formatCurrency(balanceInfo.balance!)}</span>
                  </p>
                  {parseResult.balanceDate && (
                    <p className="text-xs text-blue-600 mt-1">
                      Au {new Date(parseResult.balanceDate).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              )}

              {allowBalanceUpdate && selectedAccountId && !balanceInfo.hasBalance && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Solde non détecté</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Le fichier OFX ne contient pas d'information de solde. Vous pouvez saisir un solde manuellement.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      step="0.01"
                      value={manualBalance}
                      onChange={(e) => setManualBalance(e.target.value)}
                      placeholder="Ex: 5000.00"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updateBalance}
                        onChange={(e) => setUpdateBalance(e.target.checked)}
                        disabled={!manualBalance}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Mettre à jour</span>
                    </label>
                  </div>
                </div>
              )}

              <div>
                {categories.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900 mb-2">Aucune catégorie disponible</p>
                        <p className="text-sm text-amber-700 mb-3">
                          Pour pouvoir catégoriser vos opérations, vous devez d'abord créer des catégories.
                        </p>
                        <button
                          onClick={createDefaultCategories}
                          disabled={creatingCategories}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          {creatingCategories ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Création en cours...
                            </>
                          ) : (
                            <>
                              <Zap className="h-4 w-4" />
                              Créer les catégories par défaut
                            </>
                          )}
                        </button>
                        <p className="text-xs text-amber-600 mt-2">
                          Cela créera 9 catégories de base (subventions, ventes, salaires, etc.)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showCategorization}
                          onChange={(e) => setShowCategorization(e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Catégoriser maintenant</span>
                      </label>
                  {showCategorization && (
                    <div className="flex items-center gap-2">
                      {loadingSuggestions && (
                        <div className="flex items-center gap-2 text-purple-600 text-sm">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-300 border-t-purple-600"></div>
                          <span>Analyse intelligente en cours...</span>
                        </div>
                      )}
                      {!loadingSuggestions && suggestions.size > 0 && (
                        <button
                          onClick={applyAllSuggestions}
                          className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                        >
                          <Sparkles className="h-4 w-4" />
                          Appliquer les {suggestions.size} suggestions
                        </button>
                      )}
                      {!loadingSuggestions && suggestions.size === 0 && deduplicationResult && deduplicationResult.newCount > 0 && (
                        <div className="flex items-center gap-2 text-gray-500 text-xs">
                          <AlertCircle className="h-4 w-4" />
                          <span>Aucune suggestion disponible (l'IA apprendra de vos catégorisations)</span>
                        </div>
                      )}
                      <select
                        onChange={(e) => applyCategoryToAll(e.target.value || null)}
                        className="text-sm px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="">Appliquer à toutes...</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {showCategorization && deduplicationResult && deduplicationResult.newCount > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Libellé</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-600">Montant</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Suggestion IA</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Catégorie</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {transactionsWithCategories.slice(0, 50).map((transaction, index) => {
                            const suggestion = suggestions.get(index);
                            const suggestedCategory = suggestion ? categories.find(c => c.id === suggestion.categoryId) : null;

                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {new Date(transaction.date).toLocaleDateString('fr-FR')}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {transaction.label}
                                </td>
                                <td className={`px-4 py-2 text-sm text-right font-medium ${
                                  transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {formatCurrency(transaction.amount)}
                                </td>
                                <td className="px-4 py-2">
                                  {loadingSuggestions ? (
                                    <div className="flex items-center gap-2 text-gray-400">
                                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-purple-600"></div>
                                      <span className="text-xs">Analyse...</span>
                                    </div>
                                  ) : suggestedCategory ? (
                                    <button
                                      onClick={() => applySuggestion(index)}
                                      className="flex items-center gap-2 px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-xs transition-colors"
                                      title="Cliquer pour appliquer"
                                    >
                                      <Sparkles className="h-3 w-3" />
                                      <span>{suggestedCategory.name}</span>
                                      <span className="px-1.5 py-0.5 bg-purple-200 rounded text-xs font-medium">
                                        {Math.round(suggestion.confidence * 100)}%
                                      </span>
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    value={transaction.category_id || ''}
                                    onChange={(e) => updateTransactionCategory(index, e.target.value || null)}
                                    className="text-sm px-2 py-1 border border-gray-300 rounded w-full"
                                  >
                                    <option value="">Non catégorisée</option>
                                    {categories.map((cat) => (
                                      <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {transactionsWithCategories.length > 50 && (
                      <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 text-center">
                        Affichage des 50 premières transactions sur {transactionsWithCategories.length}
                      </div>
                    )}
                  </div>
                )}

                {!showCategorization && (
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                    <AlertCircle className="h-4 w-4 inline mr-2" />
                    Les transactions seront importées sans catégorie. Vous pourrez les catégoriser depuis l'onglet Transactions.
                  </div>
                )}
              </>
            )}
          </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isImporting}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleImport}
            disabled={isImporting || !deduplicationResult || deduplicationResult.newCount === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isImporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Import en cours...
              </>
            ) : (
              <>
                <Check className="h-5 w-5" />
                Importer {deduplicationResult?.newCount || 0} transactions
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
